import {isEqual} from 'lodash';
import type {AffectedItem, FieldDescriptor, FieldOption} from './actionCardTypes';

/**
 * One field the user changed on a card, in the form the card showed it. `entity_id` and `key` name
 * the override it describes, so the gateway can leave out edits that never reached the confirm.
 */
export type EditedValue = {entity_id: string; key: string; entity: string; field: string; value: string};

const NONE = 'none';

const isEmpty = (value: unknown): boolean =>
	value === null ||
	value === undefined ||
	value === '' ||
	(typeof value === 'number' && Number.isNaN(value)) ||
	(Array.isArray(value) && value.length === 0);

const optionLabel = (options: FieldOption[] | undefined, id: unknown): string =>
	options?.find(o => String(o.id) === String(id))?.label ?? String(id);

/** Minutes as the duration field shows them: "1h 30m", "2h" or "45m". */
const formatMinutes = (minutes: number): string => {
	// Rounded first, so 119.6 reads "2h", never "1h 60m"; never below zero.
	const total = Math.max(0, Math.round(minutes));
	const h = Math.floor(total / 60);
	const m = total % 60;
	if (h === 0) return `${m}m`;
	return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/** The value as the card shows it: option ids become their names, an empty value reads "none". */
const describe = (field: FieldDescriptor, value: unknown): string => {
	if (isEmpty(value)) return NONE;
	switch (field.type) {
		case 'select':
			return optionLabel(field.options, value);
		case 'multiselect':
			return (value as unknown[]).map(id => optionLabel(field.options, id)).join(', ');
		case 'boolean':
			return value ? 'Yes' : 'No';
		case 'duration':
			return typeof value === 'number' ? formatMinutes(value) : String(value);
		case 'daterange': {
			const range = value as {start?: string | null; end?: string | null};
			return `${range.start || NONE} to ${range.end || NONE}`;
		}
		default:
			return String(value);
	}
};

/**
 * Whether a committed value is the same as before. A multiselect is compared as a set, so picking
 * the same options in another order is not an edit. Array items are compared as strings, which
 * suits the id lists multiselects hold; it is not meant for arrays of objects.
 */
export const isSameEditValue = (a: unknown, b: unknown): boolean => {
	if (Array.isArray(a) && Array.isArray(b)) {
		const sorted = (xs: unknown[]) => xs.map(String).sort();
		return isEqual(sorted(a), sorted(b));
	}
	return isEqual(a, b);
};

/**
 * The readable form of the edits a confirm sends, one entry per changed field, in
 * row and field order. The gateway puts these into the model's next turn so it
 * describes the values that were saved; only the card knows an option id's name.
 * Rows and keys the plan does not have are skipped.
 */
export const buildEditedValues = (
	affected: AffectedItem[],
	overrides: Record<string, Record<string, unknown>>
): EditedValue[] => {
	const out: EditedValue[] = [];
	for (const item of affected) {
		const changed = overrides[item.id];
		if (!changed) continue;
		for (const field of item.fields ?? []) {
			if (!(field.key in changed)) continue;
			out.push({
				entity_id: item.id,
				key: field.key,
				// The planned title on purpose: it is the name the model saw in the plan, and an
				// edited Name is listed among the fields, so the pair reads "Alpha: Name = Alpha 2".
				entity: item.title,
				field: field.label,
				value: describe(field, changed[field.key]),
			});
		}
	}
	return out;
};

/**
 * A row's title as the card shows it. A row titled by its planned name follows an edit to that
 * name, so the heading, the Name field and any outcome summary agree. A title that is not the
 * planned name (a label made unique for two rows with the same name) is left as it is.
 */
export const rowTitle = (item: AffectedItem, edits?: Record<string, unknown>): string => {
	const nameField = item.fields?.find(f => f.key === 'name');
	const editedName = edits?.name;
	return nameField && nameField.value === item.title && typeof editedName === 'string' && editedName.trim() !== ''
		? editedName
		: item.title;
};
